
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

import { useLoaderContext } from '../../../components/Loader/LoaderContext';
import {
  getRolePermissions,
  getEmployeeRolesByCode,
} from '../../auth/services/rolePermissions.service';

const SHOW_LOGOUT = false;
const ROLE_SWITCH_PENDING_KEY = 'role_switch_pending';

interface UserRole {
  roleId: number;
  roleName: string;
  isActive?: boolean;
  isDelete?: boolean;
}

interface PermissionItem {
  permissionName: string;
  permissionActive: boolean;
  permissionDelete: boolean;
  roleActive: boolean;
  roleDelete: boolean;
  rolePermissionActive: boolean;
  rolePermissionDelete: boolean;
  roleName?: string;
}

interface RolePermissionsResponse {
  permissions?: PermissionItem[];
}

const PERMISSION_ROUTES: Record<string, string> = {
  PublicReport: '/public-report',
  NTIdManagement: '/nt-id-management',
  ActivityLogReport: '/public-report/activity-log',
  SummaryReport: '/public-report/summary-report',
  AccessCreationReport: '/public-report/access-creation',
  AccessRevocationReport: '/public-report/access-revocation',
  AccessModificationReport: '/public-report/access-modification',
};

const normalizePermissionName = (permissionName: string): string => {
  const trimmed = permissionName.trim();
  const separatorIndex = trimmed.lastIndexOf('.');

  return (
    separatorIndex >= 0
      ? trimmed.substring(separatorIndex + 1)
      : trimmed
  ).trim().toLowerCase();
};

const getStoredRoles = (): UserRole[] => {
  try {
    const storedRoles = localStorage.getItem('roles');

    if (!storedRoles) {
      return [];
    }

    const parsed: unknown = JSON.parse(storedRoles);

    if (!Array.isArray(parsed)) {
      return [];
    }

    const validRoles = parsed.filter(
      (item): item is UserRole =>
        typeof item?.roleId === 'number' &&
        Number.isInteger(item.roleId) &&
        item.roleId > 0 &&
        typeof item?.roleName === 'string' &&
        item.roleName.trim().length > 0 &&
        item.isActive !== false &&
        item.isDelete !== true,
    );

    // Remove duplicate roles by roleId.
    return Array.from(
      new Map(validRoles.map((role) => [role.roleId, role])).values(),
    );
  } catch {
    return [];
  }
};

const getFirstAllowedRoute = (
  permissionNames: string[],
): string | null => {
  for (const permissionName of permissionNames) {
    const normalizedName = normalizePermissionName(permissionName);

    const routeEntry = Object.entries(PERMISSION_ROUTES).find(
      ([key]) => key.toLowerCase() === normalizedName,
    );

    if (routeEntry) {
      return routeEntry[1];
    }
  }

  return null;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoaderContext();

  const [open, setOpen] = useState(false);
  const [showRoles, setShowRoles] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);

  const [roles, setRoles] = useState<UserRole[]>(getStoredRoles);

  const [currentRole, setCurrentRole] = useState(
    () => localStorage.getItem('role') || 'Employee',
  );

  const [currentRoleId, setCurrentRoleId] = useState<number | null>(() => {
    const storedId = localStorage.getItem('roleId');

    return storedId && Number.isInteger(Number(storedId))
      ? Number(storedId)
      : null;
  });

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const employeeName = localStorage.getItem('employeeName') || 'User';

  // Fetch the latest roles from the API whenever Header mounts (including page refresh).
  useEffect(() => {
    let cancelled = false;

    const refreshRolesFromStorage = () => {
      setRoles(getStoredRoles());
      setCurrentRole(localStorage.getItem('role') || 'Employee');

      const storedId = localStorage.getItem('roleId');
      const parsedId =
        storedId && Number.isInteger(Number(storedId))
          ? Number(storedId)
          : null;

      setCurrentRoleId(parsedId);
    };

    const fetchLatestRoles = async () => {
      const employeeCode = localStorage.getItem('employeeCode')?.trim();

      if (!employeeCode) {
        console.error(
          'Cannot fetch employee roles: employeeCode is missing from localStorage.',
        );
        refreshRolesFromStorage();
        return;
      }

      try {
        // GET /role-permissions/get-by-employee-code?employeeCode=C000853
        const response = await getEmployeeRolesByCode(employeeCode);

        if (cancelled) return;

        const activeRoles: UserRole[] = Array.isArray(response)
          ? response
              .filter(
                (item) =>
                  item.isActive === true &&
                  Number.isInteger(item.roleId) &&
                  item.roleId > 0 &&
                  typeof item.roleName === 'string' &&
                  item.roleName.trim().length > 0,
              )
              .map((item) => ({
                roleId: item.roleId,
                roleName: item.roleName.trim(),
                isActive: item.isActive,
                isDelete: false,
              }))
          : [];

        // Deduplicate roles by roleId.
        const latestRoles = Array.from(
          new Map(activeRoles.map((role) => [role.roleId, role])).values(),
        );

        // Save the latest roles returned by the backend.
        localStorage.setItem('roles', JSON.stringify(latestRoles));

        // Keep the currently selected role if it is still assigned.
        const storedRoleId = Number(localStorage.getItem('roleId'));
        const storedRoleName = localStorage.getItem('role');

        const selectedRole =
          latestRoles.find(
            (role) =>
              Number.isInteger(storedRoleId) &&
              role.roleId === storedRoleId,
          ) ??
          latestRoles.find((role) => role.roleName === storedRoleName) ??
          latestRoles[0];

        if (selectedRole) {
          localStorage.setItem('role', selectedRole.roleName);
          localStorage.setItem('roleId', String(selectedRole.roleId));
        } else {
          localStorage.removeItem('roleId');
        }

        refreshRolesFromStorage();
        window.dispatchEvent(new Event('workvista-roles-updated'));
      } catch (error) {
        if (cancelled) return;

        console.error('Failed to refresh employee roles:', error);
        // If the API fails, retain the roles already stored locally.
        refreshRolesFromStorage();
      }
    };

    void fetchLatestRoles();

    const handleRoleUpdate = () => refreshRolesFromStorage();

    window.addEventListener('storage', handleRoleUpdate);
    window.addEventListener('workvista-auth-updated', handleRoleUpdate);
    window.addEventListener('workvista-roles-updated', handleRoleUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener('storage', handleRoleUpdate);
      window.removeEventListener('workvista-auth-updated', handleRoleUpdate);
      window.removeEventListener('workvista-roles-updated', handleRoleUpdate);
    };
  }, []);

  // Hide the loader after role-switch navigation.
  useEffect(() => {
    if (
      sessionStorage.getItem(ROLE_SWITCH_PENDING_KEY) !== 'true'
    ) {
      return;
    }

    const loaderTimeout = window.setTimeout(() => {
      hideLoader();
      sessionStorage.removeItem(ROLE_SWITCH_PENDING_KEY);
    }, 1800);

    return () => {
      window.clearTimeout(loaderTimeout);
    };
  }, [location.pathname, currentRoleId, hideLoader]);

  // Exclude only the currently selected role.
  const availableRoles = roles.filter(
    (role) => role.roleId !== currentRoleId,
  );

  // Keep the profile dropdown available even if the role list is empty.
  const hasDropdownContent = true;

  // Close menus when clicking outside the profile dropdown.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setShowRoles(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const clearManagerData = () => {
    localStorage.removeItem('ManagerEmployeeCode');
    localStorage.removeItem('selectedManagerId');
    localStorage.removeItem('selectedTeamLeadId');
    localStorage.removeItem('teamFilters');
    localStorage.removeItem('teamLeader');
    localStorage.removeItem('teamLocation');
    localStorage.removeItem('managerName');
  };

  const handleLogout = () => {
    const oneLoginUrl = 'https://sagilityhealth.onelogin.com/';

    localStorage.clear();
    sessionStorage.clear();

    window.location.replace(oneLoginUrl);
  };

  const handleRoleChange = async (role: UserRole) => {
    if (switchingRole || role.roleId === currentRoleId) {
      return;
    }

    // Preserve the existing Employee behavior.
    if (role.roleName === 'Employee') {
      localStorage.setItem('role', role.roleName);
      localStorage.setItem('roleId', String(role.roleId));
      localStorage.removeItem('rolePermissions');

      setCurrentRole(role.roleName);
      setCurrentRoleId(role.roleId);
      setOpen(false);
      setShowRoles(false);

      clearManagerData();

      sessionStorage.setItem(ROLE_SWITCH_PENDING_KEY, 'true');
      showLoader();
      navigate('/employee-dashboard');

      window.dispatchEvent(new Event('workvista-auth-updated'));
      return;
    }

    // Preserve the existing Manager behavior.
    if (role.roleName === 'Manager') {
      localStorage.setItem('role', role.roleName);
      localStorage.setItem('roleId', String(role.roleId));
      localStorage.removeItem('rolePermissions');

      setCurrentRole(role.roleName);
      setCurrentRoleId(role.roleId);
      setOpen(false);
      setShowRoles(false);

      sessionStorage.setItem(ROLE_SWITCH_PENDING_KEY, 'true');
      showLoader();
      navigate('/rm-dashboard');

      window.dispatchEvent(new Event('workvista-auth-updated'));
      return;
    }

    // All other roles use permissions returned by the backend.
    const employeeCode = localStorage.getItem('employeeCode');

    if (!employeeCode) {
      console.error(
        'Cannot switch role: employeeCode is missing from localStorage.',
      );
      return;
    }

    setSwitchingRole(true);

    try {
      const result = (await getRolePermissions(
        employeeCode,
        role.roleId,
      )) as RolePermissionsResponse;

      const permissions = Array.isArray(result?.permissions)
        ? result.permissions
        : [];

      const validPermissions = permissions.filter(
        (permission) =>
          permission.permissionActive === true &&
          permission.permissionDelete === false &&
          permission.roleActive === true &&
          permission.roleDelete === false &&
          permission.rolePermissionActive === true &&
          permission.rolePermissionDelete === false,
      );

      const allowedRoute = getFirstAllowedRoute(
        validPermissions.map((permission) => permission.permissionName),
      );

      if (!allowedRoute) {
        console.error(
          'Cannot switch role: no active permissions match a registered frontend route.',
        );
        return;
      }

      localStorage.setItem('role', role.roleName);
      localStorage.setItem('roleId', String(role.roleId));
      localStorage.setItem(
        'rolePermissions',
        JSON.stringify(validPermissions),
      );

      setCurrentRole(role.roleName);
      setCurrentRoleId(role.roleId);
      setOpen(false);
      setShowRoles(false);

      sessionStorage.setItem(ROLE_SWITCH_PENDING_KEY, 'true');
      showLoader();
      navigate(allowedRoute);

      window.dispatchEvent(new Event('workvista-auth-updated'));
    } catch (error) {
      sessionStorage.removeItem(ROLE_SWITCH_PENDING_KEY);
      hideLoader();

      console.error('Failed to switch role or load permissions:', error);
    } finally {
      setSwitchingRole(false);
    }
  };

  const initials = employeeName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((name) => name.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <header className="relative z-30 box-border flex min-h-[64px] w-full items-center border-b border-[#EFF0F1] bg-white px-[13px] py-[8px]">
      <div className="flex min-h-[48px] w-full items-center justify-between">
        <div />

        <div
          ref={dropdownRef}
          className="relative flex min-h-[48px] shrink-0 items-center justify-end"
        >
          <button
            type="button"
            aria-label="Open profile and role menu"
            aria-expanded={open}
            onClick={() => {
              if (switchingRole) {
                return;
              }

              setOpen((previous) => !previous);
              setShowRoles(false);
            }}
            className="flex min-h-[48px] shrink-0 items-center gap-3 whitespace-nowrap rounded-md px-1 py-1"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F7931E] text-[14px] font-bold leading-none text-white">
              {initials}
            </div>

            <div className="flex min-w-[90px] shrink-0 flex-col items-start justify-center">
              <span className="block whitespace-nowrap text-[14px] font-medium leading-[20px] text-black">
                {employeeName}
              </span>

              <span className="block whitespace-nowrap text-[11px] font-normal leading-[16px] text-[#667085]">
                {currentRole}
              </span>
            </div>

            <ChevronDownIcon className="h-5 w-5 shrink-0 text-black" />
          </button>

          {open && hasDropdownContent && (
            <div className="absolute right-0 top-[58px] z-[100] box-border w-[240px] min-w-[240px] rounded-[12px] border border-[#E5E7EB] bg-white p-2 shadow-lg">
              {availableRoles.length > 0 ? (
                <div className="relative">
                  <button
                    type="button"
                    disabled={switchingRole}
                    onClick={() => setShowRoles((previous) => !previous)}
                    className="flex min-h-[44px] w-full items-center rounded-[8px] bg-[#FEF2F2] px-4 py-2 text-left text-[14px] font-medium leading-[20px] text-[#B91C1C] transition-colors hover:bg-[#FEE2E2] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="whitespace-nowrap">
                      {switchingRole ? 'Switching role...' : 'Switch Over'}
                    </span>
                  </button>

                  {showRoles && !switchingRole && (
                    <div className="absolute right-full top-0 z-[110] mr-2 box-border min-w-[170px] rounded-[10px] border border-[#E5E7EB] bg-white p-2 shadow-lg">
                      {availableRoles.map((role) => (
                        <button
                          key={role.roleId}
                          type="button"
                          onClick={() => void handleRoleChange(role)}
                          className="flex min-h-[44px] w-full items-center rounded-md px-3 py-2 text-left text-[14px] leading-[20px] transition-colors hover:bg-gray-100"
                        >
                          <span className="whitespace-nowrap">
                            {role.roleName}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="px-3 py-2 text-sm text-slate-500">
                  No other roles are available for your account.
                </p>
              )}

              {SHOW_LOGOUT && (
                <>
                  {availableRoles.length > 0 && (
                    <div className="my-2 border-t border-[#E5E7EB]" />
                  )}

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex min-h-[44px] w-full items-center rounded-[8px] px-4 py-2 text-left text-[14px] font-medium leading-[20px] text-[#DC2626] transition-colors hover:bg-[#FEF2F2]"
                  >
                    <span className="whitespace-nowrap">Logout</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
